import { observer } from 'mobx-react';
import React from 'react';
import { useParams } from 'react-router';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Switch } from '@components/ui/switch';
import { TenantFeaturesContext } from '..';
import { ChallengePayoutModal } from '../challenge-payout-modal';
import { ConfirmationModal } from '@components/confirmation-modal';
import { FormSection } from '@components/form-section';
import { Label } from '@components/label';
import { Spinner } from '@components/spinner';
import { EmptyTextField } from '@components/textfield';
import { useTeamsStore } from './teams-store';

const TenantContract = observer(() => {
    const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
    const [payoutModalOpen, setPayoutModalOpen] = React.useState(false);

    const { tenantName } = useParams();
    const tenantFeatures = React.useContext(TenantFeaturesContext);

    const {
        licenseModel: {
            fields: { licensesCount, flexLicense },
            form: licenseForm
        },
        currentLicenseCount,
        currentFlexEnabled,
        tenantUpdateHandler,
        licenseSubmitHandler
    } = useTeamsStore();

    return (
        <>
            {tenantFeatures.match({
                loading: () => (
                    <div className="flex min-h-[400px] items-center justify-center">
                        <Spinner />
                    </div>
                ),
                fetched: ({ company }) => (
                    <div className="flex flex-col gap-6">
                        <FormSection
                            title="License"
                            actions={
                                <Button
                                    size="sm"
                                    onClick={async () => {
                                        const formValidation =
                                            await licenseForm.validate();
                                        if (!formValidation.hasError) {
                                            setConfirmModalOpen(true);
                                        }
                                    }}
                                    disabled={tenantUpdateHandler.loading}
                                >
                                    Save
                                </Button>
                            }
                        >
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="flex flex-col gap-1.5">
                                    <Label>Flex License</Label>
                                    <Switch
                                        id="flex-license"
                                        checked={flexLicense.value}
                                        onCheckedChange={(checked) => {
                                            flexLicense.onChange(checked);
                                        }}
                                        name="license"
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <Label>Participants</Label>
                                    <span className="text-sm">
                                        {company.employeeCount}
                                    </span>
                                </div>
                                {!flexLicense.value && (
                                    <div className="flex flex-col gap-1.5">
                                        <Label>License count</Label>
                                        <EmptyTextField
                                            type="number"
                                            value={licensesCount.value}
                                            error={licensesCount.error}
                                            onChange={(val: number) => {
                                                licensesCount.onChange(val);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </FormSection>

                        <FormSection title="Actions">
                            <Button
                                variant="outline"
                                className="text-warning"
                                onClick={() => setPayoutModalOpen(true)}
                            >
                                Challenge Payout
                            </Button>
                        </FormSection>

                        <ConfirmationModal
                            open={confirmModalOpen}
                            submitHandler={licenseSubmitHandler}
                            onModalClose={() => void setConfirmModalOpen(false)}
                        >
                            <p className="text-lg font-semibold">
                                {currentFlexEnabled !== flexLicense.value
                                    ? 'Are you sure you want to switch to flex license model?'
                                    : `Are you sure you want to change the license count from ${currentLicenseCount} to ${licensesCount.value}`}
                            </p>

                            <Alert variant="warning">
                                <AlertDescription>
                                    Changes have a severe impact on finances
                                </AlertDescription>
                            </Alert>
                        </ConfirmationModal>

                        <ChallengePayoutModal
                            open={payoutModalOpen}
                            onModalClose={() => setPayoutModalOpen(false)}
                            tenantName={tenantName}
                        />
                    </div>
                )
            })}
        </>
    );
});

export default TenantContract;
